import React, { useState, useEffect } from 'react';
import { reportAPI } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';

// Icons
const MoodIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const StressIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const SleepIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
  </svg>
);

const TrendUpIcon = () => (
  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const TrendDownIcon = () => (
  <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
  </svg>
);

const TrendStableIcon = () => (
  <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const InsightsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
  </svg>
);

const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    generateReport();
  }, []);

  const generateReport = async () => {
    setLoading(true);
    try {
      const response = await reportAPI.generate({});
      console.log('Report data received:', response.data.data);
      setReportData(response.data.data);
    } catch (error) {
      console.error('Failed to generate report:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    if (!trend) return <TrendStableIcon />;
    const trendLower = trend.toLowerCase();
    if (trendLower.includes('up') || trendLower.includes('improving')) return <TrendUpIcon />;
    if (trendLower.includes('down') || trendLower.includes('declining')) return <TrendDownIcon />;
    return <TrendStableIcon />;
  };

  const getHealthColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getHealthBgColor = (score) => {
    if (score >= 80) return 'bg-green-50 border-green-200';
    if (score >= 60) return 'bg-yellow-50 border-yellow-200';
    if (score >= 40) return 'bg-orange-50 border-orange-200';
    return 'bg-red-50 border-red-200';
  };

  const CircularProgress = ({ percentage, size = 120, strokeWidth = 8, color }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (percentage / 100) * circumference;

    return (
      <div className="relative inline-flex items-center justify-center">
        <svg className="transform -rotate-90" width={size} height={size}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
            fill="none"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={color}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-2xl font-bold ${getHealthColor(percentage)}`}>
            {Math.round(percentage)}%
          </span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="mb-8">
            <div className="w-24 h-24 bg-gray-200 rounded-full mx-auto flex items-center justify-center mb-4">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">No Report Available</h2>
            <p className="text-gray-600 mb-6">Generate your first wellness report to see personalized insights and recommendations.</p>
          </div>
          <button 
            onClick={generateReport}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Generate Your Report
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white shadow-lg">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-2">Your Wellness Report</h1>
          <div className="flex items-center justify-center space-x-4 text-blue-100">
            <span className="flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {reportData.report_period?.start} → {reportData.report_period?.end}
            </span>
            <span className="flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Generated {new Date(reportData.generated_at).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>

      {/* Executive Summary Card */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex flex-col md:flex-row items-center justify-between">
          <div className="mb-6 md:mb-0">
            <h2 className="text-xl font-semibold mb-4">Executive Summary</h2>
            <p className="text-gray-700 leading-relaxed max-w-2xl">
              {reportData.descriptive_health_report?.executive_summary}
            </p>
          </div>
          <div className="flex-shrink-0">
            <CircularProgress 
              percentage={reportData.health_score || 75} 
              size={120}
              strokeWidth={8}
              color="#10b981"
            />
            <p className="text-center mt-3 text-sm text-gray-600">Overall Health Score</p>
          </div>
        </div>
      </div>

      {/* Health Metrics Cards */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-xl font-semibold mb-6">Your Health Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Mood Card */}
          <div className={`bg-gray-50 rounded-xl p-4 border ${getHealthBgColor((reportData.health_metrics?.mood?.average || 0) * 10)}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-blue-100 rounded-full">
                <MoodIcon />
              </div>
              {getTrendIcon(reportData.health_metrics?.mood?.trend)}
            </div>
            <div className={`text-2xl font-bold mb-2 ${getHealthColor((reportData.health_metrics?.mood?.average || 0) * 10)}`}>
              {typeof (reportData.health_metrics?.mood?.average || 0) === 'number' ? (reportData.health_metrics?.mood?.average || 0).toFixed(1) : (reportData.health_metrics?.mood?.average || 0)}/10
            </div>
            <div className="text-gray-600 font-medium">Mood Score</div>
            <div className="text-sm text-gray-500 mt-1">Daily average mood</div>
          </div>

          {/* Stress Card */}
          <div className={`bg-gray-50 rounded-xl p-4 border ${getHealthBgColor((10 - (reportData.health_metrics?.stress?.average || 0)) * 10)}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-orange-100 rounded-full">
                <StressIcon />
              </div>
              {getTrendIcon(reportData.health_metrics?.stress?.trend)}
            </div>
            <div className={`text-2xl font-bold mb-2 ${getHealthColor((10 - (reportData.health_metrics?.stress?.average || 0)) * 10)}`}>
              {typeof (reportData.health_metrics?.stress?.average || 0) === 'number' ? (reportData.health_metrics?.stress?.average || 0).toFixed(1) : (reportData.health_metrics?.stress?.average || 0)}/10
            </div>
            <div className="text-gray-600 font-medium">Stress Level</div>
            <div className="text-sm text-gray-500 mt-1">Lower is better</div>
          </div>

          {/* Sleep Card */}
          <div className={`bg-gray-50 rounded-xl p-4 border ${getHealthBgColor((reportData.health_metrics?.sleep?.average_hours || 0) * 10)}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-purple-100 rounded-full">
                <SleepIcon />
              </div>
              {getTrendIcon(reportData.health_metrics?.sleep?.trend)}
            </div>
            <div className={`text-2xl font-bold mb-2 ${getHealthColor((reportData.health_metrics?.sleep?.average_hours || 0) * 10)}`}>
              {typeof (reportData.health_metrics?.sleep?.average_hours || 0) === 'number' ? (reportData.health_metrics?.sleep?.average_hours || 0).toFixed(1) : (reportData.health_metrics?.sleep?.average_hours || 0)} hrs
            </div>
            <div className="text-gray-600 font-medium">Sleep Duration</div>
            <div className="text-sm text-gray-500 mt-1">Quality: {typeof (reportData.health_metrics?.sleep?.quality_score || 0) === 'number' ? (reportData.health_metrics?.sleep?.quality_score || 0).toFixed(0) : (reportData.health_metrics?.sleep?.quality_score || 0)}%</div>
          </div>
        </div>
      </div>

      {/* Analysis Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Strengths */}
        {reportData.ai_insights?.strengths?.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-green-100 rounded-full mr-3">
                <CheckIcon />
              </div>
              <h3 className="text-xl font-semibold text-green-700">Your Strengths</h3>
            </div>
            <div className="space-y-3">
              {reportData.ai_insights.strengths.map((strength, index) => (
                <div key={index} className="bg-green-50 rounded-lg p-3 border border-green-200">
                  <h4 className="font-medium text-green-800 mb-1">{strength.category}</h4>
                  <p className="text-green-700 text-sm">{strength.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Wellness Score Card - Fills the gap */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center mb-4">
            <div className="p-2 bg-blue-100 rounded-full mr-3">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-blue-700">Wellness Overview</h3>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Recovery Stage</span>
              <span className="font-medium text-gray-900">{reportData.addiction_recovery_analysis?.recovery_stage?.stage || 'Active Recovery'}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Risk Level</span>
              <span className={`font-medium ${
                reportData.addiction_recovery_analysis?.risk_assessment?.risk_level === 'low' ? 'text-green-600' :
                reportData.addiction_recovery_analysis?.risk_assessment?.risk_level === 'moderate' ? 'text-yellow-600' :
                'text-red-600'
              }`}>
                {reportData.addiction_recovery_analysis?.risk_assessment?.risk_level || 'Moderate'}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Trajectory</span>
              <span className={`font-medium ${
                reportData.addiction_recovery_analysis?.recovery_trajectory?.trajectory === 'positive' ? 'text-green-600' :
                reportData.addiction_recovery_analysis?.recovery_trajectory?.trajectory === 'concerning' ? 'text-red-600' :
                'text-yellow-600'
              }`}>
                {reportData.addiction_recovery_analysis?.recovery_trajectory?.trajectory || 'Stable'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Concerns */}
      {reportData.ai_insights?.primary_concerns?.length > 0 && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center mb-4">
            <div className="p-2 bg-orange-100 rounded-full mr-3">
              <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-orange-700">Areas to Focus On</h3>
          </div>
          <div className="space-y-3">
            {reportData.ai_insights.primary_concerns.map((concern, index) => (
              <div key={index} className="bg-orange-50 rounded-lg p-3 border-l-4 border-orange-400">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-medium text-orange-800">{concern.category}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    concern.severity === 'high' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {concern.severity} priority
                  </span>
                </div>
                <p className="text-orange-700 text-sm">{concern.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Insights Section */}
      {reportData.ai_insights?.insights?.length > 0 && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center mb-4">
            <div className="p-2 bg-purple-100 rounded-full mr-3">
              <InsightsIcon />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Key Insights</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {reportData.ai_insights.insights.map((insight, index) => (
              <div key={index} className="flex items-start p-3 bg-purple-50 rounded-lg">
                <div className="p-1 bg-purple-200 rounded-full mr-3 mt-1">
                  <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">{insight}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Daily Routine Timeline */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-xl font-semibold mb-6">Your Personalized Daily Routine</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Morning */}
          <div>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-yellow-100 rounded-full mr-3">
                <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Morning</h4>
                <p className="text-sm text-gray-600">6:00 AM - 12:00 PM</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center p-2 bg-yellow-50 rounded-lg">
                <CheckIcon />
                <span className="ml-2 text-gray-700 text-sm">Wake up at consistent time</span>
              </div>
              <div className="flex items-center p-2 bg-yellow-50 rounded-lg">
                <CheckIcon />
                <span className="ml-2 text-gray-700 text-sm">5-minute breathing exercise</span>
              </div>
              <div className="flex items-center p-2 bg-yellow-50 rounded-lg">
                <CheckIcon />
                <span className="ml-2 text-gray-700 text-sm">Drink water upon waking</span>
              </div>
            </div>
          </div>

          {/* Afternoon */}
          <div>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-green-100 rounded-full mr-3">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Afternoon</h4>
                <p className="text-sm text-gray-600">12:00 PM - 6:00 PM</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center p-2 bg-green-50 rounded-lg">
                <CheckIcon />
                <span className="ml-2 text-gray-700 text-sm">Take regular breaks</span>
              </div>
              <div className="flex items-center p-2 bg-green-50 rounded-lg">
                <CheckIcon />
                <span className="ml-2 text-gray-700 text-sm">Stay hydrated</span>
              </div>
              <div className="flex items-center p-2 bg-green-50 rounded-lg">
                <CheckIcon />
                <span className="ml-2 text-gray-700 text-sm">Mindful eating</span>
              </div>
            </div>
          </div>

          {/* Evening */}
          <div>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-indigo-100 rounded-full mr-3">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Evening</h4>
                <p className="text-sm text-gray-600">6:00 PM - 10:00 PM</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center p-2 bg-indigo-50 rounded-lg">
                <CheckIcon />
                <span className="ml-2 text-gray-700 text-sm">No screens before bed</span>
              </div>
              <div className="flex items-center p-2 bg-indigo-50 rounded-lg">
                <CheckIcon />
                <span className="ml-2 text-gray-700 text-sm">Prepare bedroom for sleep</span>
              </div>
              <div className="flex items-center p-2 bg-indigo-50 rounded-lg">
                <CheckIcon />
                <span className="ml-2 text-gray-700 text-sm">Reflect on daily progress</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-xl p-6 text-white">
        <h3 className="text-xl font-semibold mb-4">Your Next Steps</h3>
        <div className="space-y-3">
          <div className="flex items-start">
            <div className="bg-white bg-opacity-20 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">
              1
            </div>
            <p className="text-blue-100 text-sm leading-relaxed">Focus on your daily check-ins to build consistent tracking habits</p>
          </div>
          <div className="flex items-start">
            <div className="bg-white bg-opacity-20 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">
              2
            </div>
            <p className="text-blue-100 text-sm leading-relaxed">Build consistent sleep habits by following your personalized evening routine</p>
          </div>
          <div className="flex items-start">
            <div className="bg-white bg-opacity-20 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">
              3
            </div>
            <p className="text-blue-100 text-sm leading-relaxed">Practice stress management techniques during high-pressure moments</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
